//@flow

import React, { useState, useContext } from "react";
import { Form } from "react-final-form";
import classnames from "classnames";
import setFieldData from "final-form-set-field-data";

import { useTranslation } from "react-i18next";

import ShortFormSharedContext from "../../../misc/context";

import RegisterStep from "modules/ShortOfferForm/components/RegisterStep";
import { Row } from "../CommonComponents";
import NextButton from "./components/NextButton";
import PreviousButton from "./components/PreviousButton";
import Confirmation from "./components/Confirmation";

import type { ChildrenT } from "../../../misc/types";
import { scrollToFormTop } from "modules/ShortForm/misc/utilities";

import { shortOfferFormStepProgression } from "Commons/Utilities/GoogleTagManager";

import "./MultiStepper.scss";

const MultiStepper = ({ children, transitionWrapper }: { children: ChildrenT, transitionWrapper: any }) => {
  const {
    category: { categoryType },
    loginOrRegisterStep: { showLoginOrRegister, setShowLoginOrRegister },
    onFormFinalSubmit,
    user,
    inheritedStyles,
    steps: { step, setFormStep },
    configurationData: { stepsDependencies, initialValues },
    confirmation: { confirmed },
  } = useContext(ShortFormSharedContext);

  const loggedUser = user?.user;

  /* Translations for LoginOrRegister component */
  const { t } = useTranslation("blogArticle");

  /* Steps logic */
  const [showMultiStepper, setShowMultiStepper] = useState(true);
  // An additional element null allows us to sync steps count and steps in the array index
  const extractedSteps = [null, ...React.Children.toArray(children)];
  const extractedStepsCount = React.Children.count(children);
  const isLastStep = step === extractedStepsCount;
  const activeMultiStepperStep = extractedSteps[step];

  const scrollToTopTransition = () => {
    if (window.innerWidth < 768) scrollToFormTop(16);
  };

  const transition = (type: string) => {
    scrollToTopTransition();
    transitionWrapper.current.classList.add("transitioning");

    setTimeout(() => {
      if (type === "next") setFormStep(step + 1);
      if (type === "previous") {
        if (step - 1 > 0) setFormStep(step - 1);
      }
      transitionWrapper.current.classList.remove("transitioning");
    }, 400);
  };

  const handleNextClick = (values) => {
    if (isLastStep) {
      setFormStep(step + 1);
      setShowMultiStepper(false);
      if (loggedUser) {
        // Case when user is already logged since step 1
        scrollToTopTransition();
        const userId = { userId: user.user?.id };
        shortOfferFormStepProgression("step_final");
        return onFormFinalSubmit({ ...userId, ...values });
      } else {
        scrollToTopTransition();
        setShowLoginOrRegister(true);
        shortOfferFormStepProgression("step_loginOrRegister");
      }
    } else {
      transition("next");
    }
  };

  const handlePreviousClick = () => {
    transition("previous");
  };

  const handleLoginOrRegisterSubmit = (values) => {
    // Case when user is not already logged or registered
    setShowMultiStepper(false);
    shortOfferFormStepProgression("step_final");
    return onFormFinalSubmit(values);
  };

  /* Styles */
  const {
    formRow,
    formRowPaired,
    formStepButton,
    formStepButtonNext,
    formStepButtonPrevious,
    registerContainer,
  } = inheritedStyles;

  /*const showFieldValues = (values) => {
    const { locations, ...filtered } = values;
    return JSON.stringify(filtered, 0, 2);
  };*/

  return (
    <Form initialValues={initialValues?.[categoryType]} onSubmit={handleNextClick} mutators={{ setFieldData }}>
      {({ handleSubmit, submitting, values, hasValidationErrors }) => (
        // { handleSubmit } refers to the onSubmit props of the Form component
        // In our case, handleSubmit triggers handleNextClick
        <>
          {showMultiStepper && !confirmed && (
            <form onSubmit={handleSubmit} id="sofMultiStepperForm">
              {/* active step */}
              {activeMultiStepperStep}
              {/* buttons */}
              <Row className={classnames(formRow, formRowPaired)}>
                <NextButton
                  className={classnames(formStepButton, formStepButtonNext)}
                  disabled={hasValidationErrors}
                  fullWidth
                  submit
                  onClick={() => {
                    shortOfferFormStepProgression("step_" + step);
                  }}
                >
                  {stepsDependencies[step - 1].buttons.next}
                </NextButton>
              </Row>
              <Row className={classnames(formRow, formRowPaired)}>
                {step > 1 && (
                  <PreviousButton className={formStepButtonPrevious} onClick={handlePreviousClick}>
                    {stepsDependencies[step - 1].buttons.previous}
                  </PreviousButton>
                )}
              </Row>
            </form>
          )}
          {!confirmed && showLoginOrRegister && !loggedUser && (
            <div className={registerContainer}>
              <RegisterStep
                t={t}
                stackButtons={true}
                lite={true}
                onRegister={(userId) => handleLoginOrRegisterSubmit({ userId, ...values })}
              />
            </div>
          )}
          {confirmed && <Confirmation />}
          {/*<div className={inheritedStyles.json}>
            <pre>{showFieldValues(values)}</pre>
          </div>*/}
        </>
      )}
    </Form>
  );
};

const Step = ({ children }) => children;

MultiStepper.Step = Step;

export default MultiStepper;
